'use client';
import { FC, MouseEvent } from 'react';
import Link from 'next/link';
import { useRouter } from 'next/navigation';

const plans = [
    {
        planValue: 'bronze',
        name: 'Plano Bronze',
        price: 'R$ 999',
        period: '/mês',
        description: 'Ideal para empresas que estão iniciando sua jornada de inclusão.',
        features: [
            'Até 5 vagas/mês',
            'Acesso aos currículos inscritos'
        ],
        buttonText: 'Começar agora',
        highlighted: false,
        borderClass: 'border-[#fde68a]',
        icon: (
            <svg xmlns="http://www.w3.org/2000/svg" width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"/></svg>
        ),
        iconBg: 'bg-[#f97316]'
    },
    {
        planValue: 'silver',
        name: 'Plano Silver',
        price: 'R$ 1.700',
        period: '/mês (anual)',
        description: 'Para empresas que desejam presença ativa na plataforma com volume moderado de vagas.',
        features: [
            'Até 15 vagas/mês',
            'Acesso aos currículos inscritos',
            '100 créditos de visualização/mês',
            'Publicação em massa por planilha'
        ],
        buttonText: 'Começar agora',
        highlighted: false,
        borderClass: 'border-[#e5e7eb]',
        icon: (
            <svg xmlns="http://www.w3.org/2000/svg" width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"/></svg>
        ),
        iconBg: 'bg-[#9ca3af]'
    },
    {
        planValue: 'gold',
        name: 'Plano Gold',
        tag: 'Mais Popular',
        price: 'R$ 3.300',
        period: '/mês (anual)',
        description: 'Para empresas que priorizam visibilidade contínua e maior alcance.',
        features: [
            'Publicação ilimitada de vagas',
            'Acesso aos currículos inscritos',
            '150 créditos de visualização/mês',
            'Página personalizada de vagas',
            'Logo em destaque na home',
            'Envio de vagas por e-mail e WhatsApp',
            '4 divulgações anuais em nossas redes sociais',
            'Publicação em massa por planilha',
            'Integração com ATS'
        ],
        buttonText: 'Começar agora',
        highlighted: true,
        borderClass: 'border-transparent',
        icon: (
            <svg xmlns="http://www.w3.org/2000/svg" width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="m12 3-1.912 5.813a2 2 0 0 1-1.275 1.275L3 12l5.813 1.912a2 2 0 0 1 1.275 1.275L12 21l1.912-5.813a2 2 0 0 1 1.275-1.275L21 12l-5.813-1.912a2 2 0 0 1-1.275-1.275L12 3Z"/><path d="m5 3 1 1"/><path d="m19 19 1 1"/><path d="m5 21 1-1"/><path d="m21 3-1 1"/></svg>
        ),
        iconBg: 'bg-[#0F1DD7]'
    }
];

const Pricing: FC = () => {
    const router = useRouter();

    const handlePlanClick = (planValue: string) => (event: MouseEvent<HTMLAnchorElement>) => {
        event.preventDefault();
        router.push(`/empresas?plano=${planValue}#fale-conosco`, { scroll: false });

        requestAnimationFrame(() => {
            document.getElementById('fale-conosco')?.scrollIntoView({
                behavior: 'smooth',
                block: 'start',
            });
        });
    };

    return (
        <section id="planos" className="pt-[80px] pb-[80px] bg-[#f8fafc]">
            <div className="max-w-[1280px] mx-auto px-8 text-center">
                <div className="inline-flex items-center justify-center bg-[#0F1DD7] text-white h-[36px] px-[16px] py-[8px] rounded-full text-[14px] font-medium mb-[16px]">
                    Planos
                </div>
                <h2 className="text-[#111827] text-[36px] leading-[40px] font-bold mb-[16px]">
                    Escolha o plano ideal para sua empresa
                </h2>
                <p className="text-[#4B5563] text-[20px] leading-[28px] max-w-[672px] mx-auto mb-[48px]">
                    Investir em inclusão é investir no futuro. Escolha o plano que melhor se adapta às necessidades da sua organização.
                </p>
                
                <div className="flex flex-col md:flex-row gap-[24px] lg:gap-[32px] justify-center items-stretch">
                    {plans.map((plan, idx) => (
                        <div 
                            key={idx} 
                            style={{ minHeight: '100%' }}
                            className={`relative w-full max-w-[368px] p-[32px] rounded-[24px] shadow-lg transition-all duration-300 flex flex-col items-start text-left flex-1 ${
                                plan.highlighted 
                                    ? 'bg-[#0F1DD7] text-white shadow-2xl z-10' 
                                    : `bg-white border-2 ${plan.borderClass} text-[#111827]`
                            }`}
                        >
                            {plan.tag && (
                                <div className="absolute -top-[16px] left-1/2 -translate-x-1/2 bg-white text-[#0F1DD7] px-[16px] py-[6px] rounded-full text-[14px] font-bold shadow-md flex items-center gap-1">
                                    <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="#0F1DD7" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"/></svg>
                                    {plan.tag}
                                </div>
                            )}
                            
                            {/* Top section - fixed min-height to align buttons across cards */}
                            <div className="flex flex-col w-full" style={{ minHeight: '340px' }}>
                                <div className="flex items-center gap-3 mb-[16px]">
                                    <div className={`w-14 h-14 flex-shrink-0 flex items-center justify-center rounded-xl ${plan.iconBg}`}>
                                        {plan.icon}
                                    </div>
                                    <h3 className={`font-bold ${plan.highlighted ? 'text-white' : 'text-[#111827]'}`} style={{ fontSize: '28px' }}>
                                        {plan.name}
                                    </h3>
                                </div>

                                <div className="flex items-baseline gap-1 mb-[24px]">
                                    <span 
                                        className={`font-bold ${plan.highlighted ? 'text-white' : 'text-[#111827]'}`}
                                        style={{ fontSize: '40px' }}
                                    >
                                        {plan.price}
                                    </span>
                                    <span className={`font-normal ${plan.highlighted ? 'text-white/80' : 'text-gray-500'}`} style={{ fontSize: '18px' }}>
                                        {plan.period}
                                    </span>
                                </div>

                                <p 
                                    className={`leading-relaxed flex-1 mb-[32px] ${plan.highlighted ? 'text-white/90' : 'text-gray-600'}`}
                                    style={{ fontSize: '18px' }}
                                >
                                    {plan.description}
                                </p>

                                <div className="w-full mb-[32px]">
                                    <Link
                                        href={`/empresas?plano=${plan.planValue}#fale-conosco`}
                                        scroll={false}
                                        onClick={handlePlanClick(plan.planValue)}
                                        className={`flex w-full h-[52px] items-center justify-center rounded-full font-bold transition-colors ${
                                        plan.highlighted
                                            ? 'bg-white text-[#0F1DD7] hover:bg-[#E8F2FF]'
                                            : 'bg-[#0F1DD7] text-white hover:bg-[#05318E]'
                                    }`}
                                    style={{ fontSize: '16px' }}
                                    >
                                        {plan.buttonText}
                                    </Link>
                                </div>
                            </div>

                            <p 
                                className={`font-bold mb-[24px] ${plan.highlighted ? 'text-white' : 'text-[#111827]'}`}
                                style={{ fontSize: '16px' }}
                            >
                                O que está incluso:
                            </p>

                            <ul className="space-y-[16px] w-full">
                                {plan.features.map((feature, fIdx) => (
                                    <li key={fIdx} className="flex items-center gap-3">
                                        <div className={`w-[22px] h-[22px] flex-shrink-0 flex items-center justify-center rounded-full ${
                                            plan.highlighted ? 'bg-white text-[#0F1DD7]' : 'bg-[#0F1DD7] text-white'
                                        }`}>
                                            <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"/></svg>
                                        </div>
                                        <span 
                                            className={`${plan.highlighted ? 'text-white/90' : 'text-[#374151]'}`}
                                            style={{ fontSize: '16px' }}
                                        >
                                            {feature}
                                        </span>
                                    </li>
                                ))}
                            </ul>
                        </div>
                    ))}
                </div>
                
                <p className="mt-[48px] text-gray-500 text-[18px]">
                    Precisa de um plano personalizado? 
                    <a href="#fale-conosco" className="text-[#0F1DD7] font-bold ml-1 hover:underline cursor-pointer">Entre em contato conosco</a>
                </p>
            </div>
        </section>
    );
};

export default Pricing;
